import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Camera, FileText, Plus, Minus } from 'lucide-react';
import { useAssetInspectionStore } from '../../store/assetInspectionStore';
import { AssetChecklist, ChecklistItem as ServiceChecklistItem } from '../../service/assetInspectionService';

export interface ChecklistItem {
  id: string;
  question: string;
  type: 'boolean' | 'numeric' | 'text' | 'selection';
  required: boolean;
  options?: string[];
  unit?: string;
  minValue?: number;
  maxValue?: number;
  category?: string;
}

export interface ChecklistResponse {
  itemId: string;
  question?: string;
  type?: string;
  value: string | number | boolean | null; // 'complied' | 'partial_complied' | 'not_complied' for boolean type
  status: 'pass' | 'fail' | 'attention' | 'na';
  comment?: string;
  photos?: File[];
}

interface InspectionChecklistProps {
  equipmentInventoryId: string;
  equipmentSubCategoryId?: string;
  assetType?: string;
  onResponsesChange: (responses: ChecklistResponse[]) => void;
  initialResponses?: ChecklistResponse[];
  customChecklistId?: string;
  onChecklistChange?: (checklist: AssetChecklist) => void;
}

const InspectionChecklist: React.FC<InspectionChecklistProps> = ({
  equipmentInventoryId,
  equipmentSubCategoryId,
  assetType,
  onResponsesChange,
  initialResponses = [],
  customChecklistId,
  onChecklistChange
}) => {
  const {
    suggestAssetChecklists,
    fetchAssetChecklistById
  } = useAssetInspectionStore();

  const [responses, setResponses] = useState<ChecklistResponse[]>(initialResponses);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [availableChecklists, setAvailableChecklists] = useState<AssetChecklist[]>([]);
  const [selectedChecklistId, setSelectedChecklistId] = useState<string>(customChecklistId || '');
  const [loading, setLoading] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState<AssetChecklist | null>(null);
  const [showChecklistSelector, setShowChecklistSelector] = useState(false);

  // Define checklists based on asset type
  const getChecklistForAssetType = (type: string, subtype?: string): ChecklistItem[] => {
    const baseType = type.toLowerCase();
    
    if (baseType.includes('fire extinguisher') || baseType.includes('extinguisher')) {
      if (baseType.includes('co2') || subtype?.toLowerCase().includes('co2')) {
        return [
          {
            id: 'weight_check',
            question: 'Check weight of CO2 cylinder (kg)',
            type: 'numeric',
            required: true,
            unit: 'kg',
            minValue: 0,
            maxValue: 100,
            category: 'Physical Inspection'
          },
          {
            id: 'pressure_gauge',
            question: 'Is the pressure gauge reading normal?',
            type: 'boolean',
            required: true,
            category: 'Physical Inspection'
          },
          {
            id: 'cylinder_condition',
            question: 'Cylinder external condition',
            type: 'selection',
            required: true,
            options: ['Excellent', 'Good', 'Fair', 'Poor', 'Damaged'],
            category: 'Physical Inspection'
          },
          {
            id: 'safety_pin',
            question: 'Is the safety pin intact and properly sealed?',
            type: 'boolean',
            required: true,
            category: 'Safety Check'
          },
          {
            id: 'discharge_test',
            question: 'Has discharge mechanism been tested?',
            type: 'boolean',
            required: true,
            category: 'Functional Test'
          },
          {
            id: 'location_accessibility',
            question: 'Is the extinguisher easily accessible?',
            type: 'boolean',
            required: true,
            category: 'Location Check'
          },
          {
            id: 'signage_visibility',
            question: 'Are identification signs clearly visible?',
            type: 'boolean',
            required: true,
            category: 'Location Check'
          },
          {
            id: 'maintenance_tag',
            question: 'Is maintenance tag up to date?',
            type: 'boolean',
            required: true,
            category: 'Documentation'
          },
          {
            id: 'overall_comments',
            question: 'Additional observations or comments',
            type: 'text',
            required: false,
            category: 'General'
          }
        ];
      } else if (baseType.includes('foam') || subtype?.toLowerCase().includes('foam')) {
        return [
          {
            id: 'foam_level',
            question: 'Check foam solution level (%)',
            type: 'numeric',
            required: true,
            unit: '%',
            minValue: 0,
            maxValue: 100,
            category: 'Physical Inspection'
          },
          {
            id: 'pressure_gauge',
            question: 'Is the pressure gauge reading normal?',
            type: 'boolean',
            required: true,
            category: 'Physical Inspection'
          },
          {
            id: 'nozzle_condition',
            question: 'Nozzle and hose condition',
            type: 'selection',
            required: true,
            options: ['Excellent', 'Good', 'Fair', 'Poor', 'Damaged'],
            category: 'Physical Inspection'
          },
          {
            id: 'safety_pin',
            question: 'Is the safety pin intact and properly sealed?',
            type: 'boolean',
            required: true,
            category: 'Safety Check'
          },
          {
            id: 'foam_quality',
            question: 'Is foam solution clear and free from contamination?',
            type: 'boolean',
            required: true,
            category: 'Chemical Check'
          },
          {
            id: 'location_accessibility',
            question: 'Is the extinguisher easily accessible?',
            type: 'boolean',
            required: true,
            category: 'Location Check'
          },
          {
            id: 'maintenance_tag',
            question: 'Is maintenance tag up to date?',
            type: 'boolean',
            required: true,
            category: 'Documentation'
          },
          {
            id: 'overall_comments',
            question: 'Additional observations or comments',
            type: 'text',
            required: false,
            category: 'General'
          }
        ];
      }
    } else if (baseType.includes('smoke detector') || baseType.includes('detector')) {
      return [
        {
          id: 'led_indicator',
          question: 'Is the LED indicator functioning properly?',
          type: 'boolean',
          required: true,
          category: 'Functional Test'
        },
        {
          id: 'test_button',
          question: 'Does the test button activate the alarm?',
          type: 'boolean',
          required: true,
          category: 'Functional Test'
        },
        {
          id: 'alarm_sound',
          question: 'Alarm sound level (dB)',
          type: 'numeric',
          required: true,
          unit: 'dB',
          minValue: 60,
          maxValue: 120,
          category: 'Functional Test'
        },
        {
          id: 'detector_cleanliness',
          question: 'Is the detector clean and free from dust/debris?',
          type: 'boolean',
          required: true,
          category: 'Physical Inspection'
        },
        {
          id: 'mounting_security',
          question: 'Is the detector securely mounted?',
          type: 'boolean',
          required: true,
          category: 'Physical Inspection'
        },
        {
          id: 'wiring_condition',
          question: 'Wiring condition',
          type: 'selection',
          required: true,
          options: ['Excellent', 'Good', 'Fair', 'Poor', 'Damaged'],
          category: 'Physical Inspection'
        },
        {
          id: 'location_appropriate',
          question: 'Is detector location appropriate for coverage?',
          type: 'boolean',
          required: true,
          category: 'Location Check'
        },
        {
          id: 'overall_comments',
          question: 'Additional observations or comments',
          type: 'text',
          required: false,
          category: 'General'
        }
      ];
    } else if (baseType.includes('sprinkler')) {
      return [
        {
          id: 'water_pressure',
          question: 'Water pressure reading (PSI)',
          type: 'numeric',
          required: true,
          unit: 'PSI',
          minValue: 0,
          maxValue: 300,
          category: 'Functional Test'
        },
        {
          id: 'sprinkler_head_condition',
          question: 'Sprinkler head condition',
          type: 'selection',
          required: true,
          options: ['Excellent', 'Good', 'Fair', 'Poor', 'Damaged'],
          category: 'Physical Inspection'
        },
        {
          id: 'obstruction_check',
          question: 'Are sprinkler heads free from obstructions?',
          type: 'boolean',
          required: true,
          category: 'Physical Inspection'
        },
        {
          id: 'pipe_condition',
          question: 'Visible pipe condition',
          type: 'selection',
          required: true,
          options: ['Excellent', 'Good', 'Fair', 'Poor', 'Corroded'],
          category: 'Physical Inspection'
        },
        {
          id: 'valve_operation',
          question: 'Do control valves operate smoothly?',
          type: 'boolean',
          required: true,
          category: 'Functional Test'
        },
        {
          id: 'alarm_test',
          question: 'Does water flow alarm activate properly?',
          type: 'boolean',
          required: true,
          category: 'Functional Test'
        },
        {
          id: 'coverage_adequate',
          question: 'Is coverage area adequate?',
          type: 'boolean',
          required: true,
          category: 'Location Check'
        },
        {
          id: 'overall_comments',
          question: 'Additional observations or comments',
          type: 'text',
          required: false,
          category: 'General'
        }
      ];
    } else if (baseType.includes('chemical') || baseType.includes('hazardous')) {
      return [
        {
          id: 'container_integrity',
          question: 'Container integrity check',
          type: 'selection',
          required: true,
          options: ['Excellent', 'Good', 'Fair', 'Poor', 'Compromised'],
          category: 'Physical Inspection'
        },
        {
          id: 'labeling_complete',
          question: 'Are all safety labels present and legible?',
          type: 'boolean',
          required: true,
          category: 'Safety Check'
        },
        {
          id: 'sds_available',
          question: 'Is Safety Data Sheet (SDS) available?',
          type: 'boolean',
          required: true,
          category: 'Documentation'
        },
        {
          id: 'storage_temperature',
          question: 'Storage temperature (°C)',
          type: 'numeric',
          required: true,
          unit: '°C',
          minValue: -50,
          maxValue: 100,
          category: 'Environmental'
        },
        {
          id: 'ventilation_adequate',
          question: 'Is ventilation adequate?',
          type: 'boolean',
          required: true,
          category: 'Environmental'
        },
        {
          id: 'spill_kit_available',
          question: 'Is spill response kit available?',
          type: 'boolean',
          required: true,
          category: 'Safety Check'
        },
        {
          id: 'ppe_available',
          question: 'Is appropriate PPE available?',
          type: 'boolean',
          required: true,
          category: 'Safety Check'
        },
        {
          id: 'expiry_date_check',
          question: 'Is material within expiry date?',
          type: 'boolean',
          required: true,
          category: 'Documentation'
        },
        {
          id: 'overall_comments',
          question: 'Additional observations or comments',
          type: 'text',
          required: false,
          category: 'General'
        }
      ];
    }
    
    // Default generic checklist
    return [
      {
        id: 'physical_condition',
        question: 'Overall physical condition',
        type: 'selection',
        required: true,
        options: ['Excellent', 'Good', 'Fair', 'Poor', 'Damaged'],
        category: 'Physical Inspection'
      },
      {
        id: 'functionality_test',
        question: 'Does the equipment function properly?',
        type: 'boolean',
        required: true,
        category: 'Functional Test'
      },
      {
        id: 'safety_compliance',
        question: 'Does equipment meet safety requirements?',
        type: 'boolean',
        required: true,
        category: 'Safety Check'
      },
      {
        id: 'location_appropriate',
        question: 'Is equipment location appropriate?',
        type: 'boolean',
        required: true,
        category: 'Location Check'
      },
      {
        id: 'overall_comments',
        question: 'Additional observations or comments',
        type: 'text',
        required: false,
        category: 'General'
      }
    ];
  };

  useEffect(() => {
    fetchAvailableChecklists();
  }, [equipmentInventoryId, equipmentSubCategoryId, assetType]);

  useEffect(() => {
    if (selectedChecklistId) {
      loadSelectedChecklist();
    }
  }, [selectedChecklistId]);

  const fetchAvailableChecklists = async () => {
    if (!equipmentInventoryId) {
      console.warn('No equipment inventory ID provided');
      return;
    }

    try {
      setLoading(true);
      
      // Fetch suggested checklists from backend based on category, subcategory, and type
      const suggested = await suggestAssetChecklists(
        equipmentInventoryId, 
        equipmentSubCategoryId, 
        assetType
      );
      
      if (suggested && suggested.length > 0) {
        setAvailableChecklists(suggested);
        
        // Auto-select the first (best matching) checklist
        const defaultChecklist = suggested[0];
        setSelectedChecklistId(defaultChecklist._id || defaultChecklist.id || '');
        setSelectedChecklist(defaultChecklist);
      } else {
        // No matching checklist found
        console.warn('No matching checklist found for this asset');
        setChecklist([]);
      }
      
    } catch (error) {
      console.error('Error fetching checklists:', error);
      setChecklist([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSelectedChecklist = async () => {
    try {
      if (!selectedChecklistId) {
        return;
      }

      // Find the selected checklist from available checklists
      const selectedTemplate = availableChecklists.find(
        c => (c._id === selectedChecklistId || c.id === selectedChecklistId)
      );
      
      if (selectedTemplate) {
        setSelectedChecklist(selectedTemplate);
        const convertedChecklist = convertTemplateToChecklist(selectedTemplate);
        setChecklist(convertedChecklist);
        onChecklistChange?.(selectedTemplate);
        initializeResponses(convertedChecklist);
      }
    } catch (error) {
      console.error('Error loading selected checklist:', error);
    }
  };

  const convertTemplateToChecklist = (template: AssetChecklist): ChecklistItem[] => {
    return template.items
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(item => ({
        id: item.id,
        question: item.question,
        type: item.type,
        required: item.required,
        options: item.options,
        unit: item.unit,
        minValue: item.minValue,
        maxValue: item.maxValue,
        category: item.category || 'General'
      }));
  };

  const initializeResponses = (checklistItems: ChecklistItem[]) => {
    if (initialResponses.length === 0) {
      const newResponses = checklistItems.map(item => ({
        itemId: item.id,
        question: item.question,
        type: item.type,
        value: '',
        status: 'attention' as const,
        comment: '',
        photos: []
      }));
      setResponses(newResponses);
      onResponsesChange(newResponses);
    }
  };

  const updateResponse = (itemId: string, updates: Partial<ChecklistResponse>) => {
    const newResponses = responses.map(response => 
      response.itemId === itemId 
        ? { ...response, ...updates }
        : response
    );
    setResponses(newResponses);
    onResponsesChange(newResponses);
  };

  const getStatusFromValue = (item: ChecklistItem, value: any): 'pass' | 'fail' | 'attention' | 'na' => {
    // Handle N/A case
    if (value === null || value === 'na') {
      return 'na';
    }
    
    if (item.type === 'boolean') {
      // Handle new compliance values
      if (value === 'complied' || value === true) return 'pass';
      if (value === 'not_complied' || value === false) return 'fail';
      if (value === 'partial_complied') return 'attention';
      return 'attention';
    } else if (item.type === 'numeric') {
      const numValue = Number(value);
      if (item.minValue !== undefined && numValue < item.minValue) return 'fail';
      if (item.maxValue !== undefined && numValue > item.maxValue) return 'fail';
      return numValue > 0 ? 'pass' : 'attention';
    } else if (item.type === 'selection') {
      const stringValue = String(value).toLowerCase();
      if (stringValue.includes('excellent') || stringValue.includes('good')) return 'pass';
      if (stringValue.includes('poor') || stringValue.includes('damaged')) return 'fail';
      return 'attention';
    }
    return value ? 'pass' : 'attention';
  };

  const handleValueChange = (itemId: string, value: any) => {
    const item = checklist.find(i => i.id === itemId);
    if (!item) return;
    
    const status = getStatusFromValue(item, value);
    updateResponse(itemId, { value, status });
  };

  const handlePhotoUpload = (itemId: string, files: FileList) => {
    const response = responses.find(r => r.itemId === itemId);
    if (!response) return;
    
    const newPhotos = [...(response.photos || []), ...Array.from(files)];
    updateResponse(itemId, { photos: newPhotos });
  };

  const removePhoto = (itemId: string, photoIndex: number) => {
    const response = responses.find(r => r.itemId === itemId);
    if (!response || !response.photos) return;
    
    const newPhotos = response.photos.filter((_, index) => index !== photoIndex);
    updateResponse(itemId, { photos: newPhotos });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle size={20} className="text-green-500" />;
      case 'fail':
        return <XCircle size={20} className="text-red-500" />;
      case 'na':
        return <Minus size={20} className="text-gray-500" />;
      default:
        return <AlertTriangle size={20} className="text-orange-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return 'border-green-200 bg-green-50';
      case 'fail':
        return 'border-red-200 bg-red-50';
      case 'na':
        return 'border-gray-200 bg-gray-50';
      default:
        return 'border-orange-200 bg-orange-50';
    }
  };

  // 3 critical questions — exact match hardcoded values
  const CRITICAL_QUESTIONS = [
    'Is the smoke detector in healthy and in working condition ?',
    'Refilling of Extinguisher done in 5 year span or lesser period than that ?',
    'HPT/ Hydrostatic Testing done within 5 years or lesser period than that according to OEM?',
  ];

  const isCriticalQuestion = (question: string): boolean =>
    CRITICAL_QUESTIONS.some((cq) => cq.toLowerCase() === question.trim().toLowerCase());

  // Group checklist items by category
  const groupedChecklist = checklist.reduce((acc, item) => {
    const category = item.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {} as Record<string, ChecklistItem[]>);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Inspection Checklist
            </h2>
            {selectedChecklist && (
              <p className="text-sm text-gray-600 mt-1">
                {selectedChecklist.name}
              </p>
            )}
          </div>
          
          {availableChecklists.length > 0 && (
            <div className="flex items-center gap-3">
              <select
                value={selectedChecklistId}
                onChange={(e) => setSelectedChecklistId(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1"
                disabled={loading}
              >
                <option value="">Select Checklist</option>
                {availableChecklists.map((template) => (
                  <option key={template._id || template.id} value={template._id || template.id}>
                    {template.name} {!template.isActive && '(Inactive)'}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        {loading && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading checklist...</p>
          </div>
        )}

        {!loading && checklist.length > 0 && selectedChecklist && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-800">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium">
                Using checklist: <strong>{selectedChecklist.name}</strong>
              </span>
            </div>
            <p className="text-xs text-green-700 mt-1">
              {selectedChecklist.equipmentInventoryName && `Category: ${selectedChecklist.equipmentInventoryName}`}
              {selectedChecklist.equipmentSubCategoryName && ` • Subcategory: ${selectedChecklist.equipmentSubCategoryName}`}
              {selectedChecklist.assetType && ` • Type: ${selectedChecklist.assetType}`}
            </p>
          </div>
        )}
        
        {Object.entries(groupedChecklist).map(([category, items]) => (
          <div key={category} className="mb-8">
            <h3 className="text-lg font-medium text-gray-700 mb-4 pb-2 border-b border-gray-200">
              {category}
            </h3>
            
            <div className="space-y-4">
              {items.map((item) => {
                const response = responses.find(r => r.itemId === item.id);
                if (!response) return null;
                
                const critical = isCriticalQuestion(item.question);
                return (
                  <div
                    key={item.id}
                    className={`rounded-lg p-4 transition-colors ${
                      critical
                        ? 'bg-amber-50 border-2 border-amber-400 border-l-4 !border-l-amber-600 shadow-md'
                        : `border ${getStatusColor(response.status)}`
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusIcon(response.status)}
                          <h4 className={`font-medium ${critical ? 'text-amber-900' : 'text-gray-800'}`}>
                            {item.question}
                            {item.required && <span className="text-red-500 ml-1">*</span>}
                          </h4>
                          {critical && (
                            <span className="ml-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-200 text-amber-800 border border-amber-400 whitespace-nowrap">
                              ⚠ CRITICAL
                            </span>
                          )}
                        </div>
                        
                        {/* Input based on type */}
                        <div className="mt-2">
                          {item.type === 'boolean' && (
                            <div className="flex flex-wrap gap-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={item.id}
                                  checked={response.value === 'complied' || response.value === true}
                                  onChange={() => handleValueChange(item.id, 'complied')}
                                  className="text-green-600 cursor-pointer w-4 h-4"
                                />
                                <span className="text-green-700 font-medium">Complied</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={item.id}
                                  checked={response.value === 'partial_complied'}
                                  onChange={() => handleValueChange(item.id, 'partial_complied')}
                                  className="text-orange-600 cursor-pointer w-4 h-4"
                                />
                                <span className="text-orange-700 font-medium">Partial Complied</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={item.id}
                                  checked={response.value === 'not_complied' || response.value === false}
                                  onChange={() => handleValueChange(item.id, 'not_complied')}
                                  className="text-red-600 cursor-pointer w-4 h-4"
                                />
                                <span className="text-red-700 font-medium">Not Complied</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={item.id}
                                  checked={response.value === null || response.value === 'na'}
                                  onChange={() => handleValueChange(item.id, null)}
                                  className="text-gray-600 cursor-pointer w-4 h-4"
                                />
                                <span className="text-gray-600 font-medium">Not Applicable</span>
                              </label>
                            </div>
                          )}
                          
                          {item.type === 'numeric' && (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={response.value as number || ''}
                                onChange={(e) => handleValueChange(item.id, Number(e.target.value))}
                                min={item.minValue}
                                max={item.maxValue}
                                className="border border-gray-300 rounded px-3 py-2 w-32"
                                placeholder="Enter value"
                              />
                              {item.unit && <span className="text-gray-500">{item.unit}</span>}
                              {(item.minValue !== undefined || item.maxValue !== undefined) && (
                                <span className="text-xs text-gray-500">
                                  ({item.minValue || 0} - {item.maxValue || '∞'})
                                </span>
                              )}
                            </div>
                          )}
                          
                          {item.type === 'selection' && (
                            <select
                              value={response.value as string || ''}
                              onChange={(e) => handleValueChange(item.id, e.target.value)}
                              className="border border-gray-300 rounded px-3 py-2 w-full max-w-xs"
                            >
                              <option value="">-- Select option --</option>
                              {item.options?.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          )}
                          
                          {item.type === 'text' && (
                            <textarea
                              value={response.value as string || ''}
                              onChange={(e) => handleValueChange(item.id, e.target.value)}
                              className="border border-gray-300 rounded px-3 py-2 w-full"
                              rows={3}
                              placeholder="Enter your observations..."
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Comments */}
                    {(() => {
                      const requiresComment =
                        response.value === 'not_complied' ||
                        response.value === 'partial_complied' ||
                        response.value === false;
                      const isMissing = requiresComment && !response.comment?.trim();
                      return (
                        <div className="mt-3">
                          <label className="block text-sm text-gray-600 mb-1">
                            Additional Comments {requiresComment && <span className="text-red-500 font-medium">*</span>}
                          </label>
                          <textarea
                            value={response.comment || ''}
                            onChange={(e) => updateResponse(item.id, { comment: e.target.value })}
                            rows={2}
                            className={`border rounded px-3 py-2 w-full resize-y ${
                              isMissing
                                ? 'border-red-400 bg-red-50 focus:ring-red-500 placeholder-red-400'
                                : 'border-gray-300 focus:ring-blue-500'
                            } focus:outline-none focus:ring-2 focus:border-transparent`}
                            placeholder={requiresComment ? 'Comments required — explain non-compliance…' : 'Optional comments...'}
                          />
                          {isMissing && (
                            <p className="text-xs text-red-500 mt-1">Comments are mandatory for non-compliant items</p>
                          )}
                        </div>
                      );
                    })()}
                    
                    {/* Photo Upload */}
                    {/* <div className="mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm text-gray-600">
                          Photos (Optional)
                        </label>
                        <label className="cursor-pointer flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
                          <Camera size={16} />
                          <span>Add Photo</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => e.target.files && handlePhotoUpload(item.id, e.target.files)}
                            className="hidden"
                          />
                        </label>
                      </div>
                      
                      {response.photos && response.photos.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {response.photos.map((photo, index) => (
                            <div key={index} className="relative">
                              <img
                                src={URL.createObjectURL(photo)}
                                alt={`Photo ${index + 1}`}
                                className="w-16 h-16 object-cover rounded border"
                              />
                              <button
                                onClick={() => removePhoto(item.id, index)}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div> */}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InspectionChecklist;
